import jsCookie from 'js-cookie';
import PROFILE from '../constants/profile';
import {
  ACTIONS,
  ADDONS,
  COOKIES,
  ERRORTYPES,
  APPINDICATORS,
  ENVIRONMENT,
} from '../constants';
import {
  validateName,
  validPhoneNumber,
  validatePhoneNumber,
  validateWebsite,
  validateCompanyName,
  trimCompanyName,
  trimWebsite,
} from '../utils/profileInput';
import { validateEmail } from '../utils/formInput';
import createRequest from '../utils/requestService';
import getEnvVariable from '../utils/environment';
import {
  setCookieWithDomain,
  getParsedCookie,
  hasIP,
  hasEase,
} from '../utils/cookieHelpers';
import accountTypes from '../constants/accountTypes';

const getEmailVolumeValue = (emailVolume) => {
  switch (emailVolume) {
    case PROFILE.LV_EMAIL:
      return PROFILE.LV_EMAIL_VAL;
    case PROFILE.MLV_EMAIL:
      return PROFILE.MLV_EMAIL_VAL;
    case PROFILE.MV_EMAIL:
      return PROFILE.MV_EMAIL_VAL;
    case PROFILE.MHV_EMAIL:
      return PROFILE.MHV_EMAIL_VAL;
    case PROFILE.HLV_EMAIL:
      return PROFILE.HLV_EMAIL_VAL;
    case PROFILE.HMV_EMAIL:
      return PROFILE.HMV_EMAIL_VAL;
    case PROFILE.HV_EMAIL:
      return PROFILE.HV_EMAIL_VAL;
    default:
      return ERRORTYPES.NO_TYPE_SET;
  }
};

export const setEhawkTalon = talon => ({
  type: ACTIONS.SET_EHAWK_TALON,
  talon,
});

export const setFirstName = firstName => ({
  type: ACTIONS.SET_FIRST_NAME,
  firstName,
});
export const blurFirstName = firstName => () => {
  const firstNameError = validateName(firstName);
  if (!firstNameError) {
    window.analytics.track('Profile first name complete');
  }
};

export const setLastName = lastName => ({
  type: ACTIONS.SET_LAST_NAME,
  lastName,
});
export const blurLastName = lastName => () => {
  const lastNameError = validateName(lastName);
  if (!lastNameError) {
    window.analytics.track('Profile last name complete');
  }
};

export const setPhoneNumber = phoneNumber => ({
  type: ACTIONS.SET_PHONE_NUMBER,
  phoneNumber,
});
export const blurPhoneNumber = phoneNumber => () => {
  const phoneNumberVal = validPhoneNumber(phoneNumber);
  const phoneNumberError = validatePhoneNumber(phoneNumberVal);
  if (!phoneNumberError) {
    window.analytics.track('Profile phone number complete');
  }
};

export const setCompanyName = companyName => ({
  type: ACTIONS.SET_COMPANY_NAME,
  companyName,
});
export const blurCompanyName = companyName => () => {
  const companyNameVal = trimCompanyName(companyName);
  const companyNameError = validateCompanyName(companyNameVal);
  if (!companyNameError) {
    window.analytics.track('Profile company complete');
  }
};

export const setWebsite = website => ({ type: ACTIONS.SET_WEBSITE, website });
export const blurWebsite = website => () => {
  const websiteVal = trimWebsite(website);
  const websiteError = validateWebsite(websiteVal);
  if (!websiteError) {
    window.analytics.track('Profile website URL complete');
  }
};

export const setEmail = email => ({ type: ACTIONS.SET_EMAIL, email });
export const blurEmail = email => () => {
  const emailError = validateEmail(email).errorType;
  if (emailError === ERRORTYPES.NO_TYPE_SET) {
    window.analytics.track('Profile email complete');
  }
};

export const setOccupation = occupation => ({
  type: ACTIONS.SET_OCCUPATION,
  occupation,
});

export const toggleIsTransactional = () => {
  window.analytics.track('Profile email type transactional selected');
  return { type: ACTIONS.TOGGLE_IS_TRANSACTIONAL };
};

export const toggleIsMarketing = () => {
  window.analytics.track('Profile email type marketing selected');
  return { type: ACTIONS.TOGGLE_IS_MARKETING };
};

export const setEmailVolume = (emailVolume) => {
  window.analytics.track('Profile expected email volume selected');
  return { type: ACTIONS.SET_EMAIL_VOLUME, emailVolume };
};

export const setEmployeeCount = (employeeCount) => {
  window.analytics.track('Profile employee count selected');
  return {
    type: ACTIONS.SET_EMPLOYEE_COUNT,
    employeeCount,
  };
};

export const checkIsFormValid = () => ({ type: ACTIONS.CHECK_IS_FORM_VALID });

export const fetchStatus = () => dispatch =>
  fetch(createRequest('/v3/s2s/signup/status', { method: 'GET' }))
    .then(response => response.json())
    .then((response) => {
      if (response.ok) {
        dispatch({
          type: ACTIONS.USER_STATUS_FETCHED,
          status: response,
        });
      }
    });

const addEase = () => fetch(createRequest('/v3/billing/message_storage'));

const saveResponse = async ({
  response,
  parsedCookie,
  history,
  dispatch,
  profileInputs,
}) => {
  const isAds = window.location.href.indexOf(accountTypes.ads) > -1;
  if (response.ok) {
    let easeReq;
    jsCookie.remove(COOKIES.SIGNUP_SESSION);
    window.analytics.track('Profile page complete');

    const formattedOccupation = profileInputs.occupation.toLowerCase();
    const profileRoleSelected = 'Profile role selected';
    // send Segment event that a profile role was selected
    window.analytics.track(profileRoleSelected);
    // create a cookie for Mako to read to promote services to specific user segments with AppCues
    setCookieWithDomain(COOKIES.USER_OCCUPATION, formattedOccupation);

    if (hasIP()) {
      window.analytics.track('IPs added', {
        value: parsedCookie.addons[ADDONS.DEDICATED_IP],
      });
    }
    if (hasEase()) {
      easeReq = await addEase();
      if (easeReq.ok) {
        window.analytics.track('EASE_ADDED', {
          value: parsedCookie.addons[ADDONS.EASE],
        });
      }
    }
    if (isAds) {
      window.location.replace(`${getEnvVariable(ENVIRONMENT.LABS_URL)}/grow`);
    } else {
      window.location.replace(`${getEnvVariable(ENVIRONMENT.MAKO_HOST_URL)}/guide?from=profile&integrate=true`);
    }
  } else if (response.status === 403) {
    jsCookie.remove(COOKIES.SIGNUP_SESSION);
    window.analytics.track('User banned as high risk');
    history.push('/declined');
  } else {
    dispatch({ type: ACTIONS.USER_PROFILE_FAILED });
    dispatch({
      type: APPINDICATORS.DISPLAY_ALERT,
      alertType: APPINDICATORS.ALERT_TYPES.DANGER,
      alertText: APPINDICATORS.ALERT_TEXT.GENERIC_ERROR,
    });
  }
  dispatch({ type: APPINDICATORS.PAGE_LOADING_FINISHED });
};

export const onSave = (profileInputs, history) => (dispatch) => {
  dispatch({ type: APPINDICATORS.PAGE_LOADING });
  const isAds = window.location.href.indexOf(accountTypes.ads) > -1;
  const userProfile = {
    first_name: profileInputs.firstName,
    last_name: profileInputs.lastName,
    phone: profileInputs.phoneNumber,
    company: profileInputs.companyName,
    website: profileInputs.website,
  };

  const payload = {
    talon: profileInputs.talon.value,
    occupation: profileInputs.occupation,
    employee_count: profileInputs.employeeCount,
    is_transactional: profileInputs.isTransactional || 0,
    is_marketing: profileInputs.isMarketing || 0,
    email_volume: profileInputs.emailVolume
      ? getEmailVolumeValue(profileInputs.emailVolume)
      : PROFILE.LV_EMAIL_VAL,
    user_profile: userProfile,
  };

  // the backend requires the employee count and occupation fields, but that ads sign-up does not
  // if the account is ads we populate it with default values
  if (isAds) {
    payload.employee_count = PROFILE.LV_EMPLOYEE;
    payload.occupation = PROFILE.OTHER;
  }

  // it's a reseller!
  if (profileInputs.email) {
    payload.email = profileInputs.email;
  }

  const token = jsCookie.get(COOKIES.MAKO_AUTH);

  const options = {
    method: 'POST',
    headers: {
      Authorization: `token ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload }),
  };
  const parsedCookie = getParsedCookie(COOKIES.SELECTED_PACKAGES);

  const result = fetch(
    `${getEnvVariable(ENVIRONMENT.MAKO_URL)}/v3/s2s/signup/provision`,
    options,
  ).then((response) => {
    const saveProvisionInfo = {
      response,
      parsedCookie,
      history,
      dispatch,
      profileInputs,
    };
    saveResponse(saveProvisionInfo);
  });

  return result;
};

export const setAdsUser = isAdsUser => ({
  type: ACTIONS.SET_ADS_USER,
  isAdsUser,
});



// WEBPACK FOOTER //
// ./src/actions/profileInputs.js